/* eslint-disable no-unused-vars */
import { Grid } from '@mui/material';
import Divider from '@mui/material/Divider';
import './skuDetails.scss';
import { FormikProvider, useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Back } from 'design_system/src/static/icons/back.svg';
import { ReactComponent as Bookmark } from 'design_system/src/static/icons/bookmark.svg';
import { ReactComponent as Calendar } from 'design_system/src/static/icons/date.svg';
import { ReactComponent as ChevronDown } from 'design_system/src/static/icons/chevron-down.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-lg.svg';
import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as Save } from 'design_system/src/static/icons/save.svg';
import { ReactComponent as Speedometer } from 'design_system/src/static/icons/speedometer.svg';
import { ReactComponent as Details } from 'design_system/src/static/icons/detail.svg';
import { ReactComponent as Images } from 'design_system/src/static/icons/image.svg';
import { ReactComponent as PriceTag } from 'design_system/src/static/icons/price-tag.svg';

import { Button, Input, SideModal, Typography } from 'design_system/src';

import LoadDocuments from 'components/hocs/loadDocuments/loadDocuments';
import useProviders from 'hooks/useProviders';
import { getBrandsWithModels } from 'models/inventory/controllers/brand.controller';
import { Model } from 'models/inventory/interfaces/brand.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { EvidenceDocument } from 'models/shared/types/documents';
import {
	addImageToSku,
	createSku,
	deleteSkuImage,
	getNextSkuVersionId,
	getSkuByCode,
	updateSku,
} from 'models/sku/controllers/sku.controller';
import { SkuImageType } from 'models/sku/enums/sku-image.enum';
import {
	CreateSkuDto,
	Sku,
	SkuImage,
	SkuStatusEnum,
} from 'models/sku/interfaces/sku.interface';
import { SkuVariant } from 'models/sku-variant/interfaces/sku-variant.interface';
import {
	addFileVehicleDetail,
	createVehicleDetails,
	deleteFileVehicleDetail,
	getVehicleDetailsBySkuId,
	updateVehicleDetails,
} from 'models/vehicle-detail/controllers/vehicle-detail.controller';
import {
	VehicleDetailImagesType,
	VehicleDetailImagesTypeValues,
} from 'models/vehicle-detail/enums/vehicle-detail.enum';
import {
	CreateVehicleDetailDto,
	VehicleDetail,
	VehicleDetailImageEntry,
} from 'models/vehicle-detail/interfaces/vehicle-detail.interface';
import motoLoading from 'static/icons/loading_moto.gif';
import { toast } from 'react-toastify';
import { vehicleColorValues } from 'models/inventory/enums/vehicle.enum';
import SkuVariantDetails, { SkuVariantFormValues } from './SkuVariantDetails';
import {
	skuDetailsDefaults,
	skuDetailsValidationSchema,
	specsDefaults,
} from './constants';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from(
	{ length: 8 },
	(_, i) => currentYear - 6 + i,
).map((year) => ({ value: year, label: String(year) }));

const mapSkuImages = (images?: SkuImage[]): EvidenceDocument<SkuImageType>[] =>
	(images ?? []).map((ele) => ({
		url: ele.url,
		name: ele.name,
		typeEvidence: ele.imageType as SkuImageType,
		mimeType: ele.mimeType,
		_id: ele._id,
		size: ele.size,
		path: ele.path,
		hash: ele.hash,
		ext: ele.ext,
		createdAt: '',
	}));

const mapDetailImages = (
	images?: VehicleDetailImageEntry[],
): EvidenceDocument<VehicleDetailImagesType>[] =>
	(images ?? []).map((ele) => ({
		url: ele.url,
		name: ele.name,
		typeEvidence: ele.imageType as VehicleDetailImagesType,
		mimeType: ele.mimeType,
		_id: ele._id,
		size: ele.size,
		path: ele.path,
		hash: ele.hash,
		ext: ele.ext,
		createdAt: '',
	}));

const upsertVariant = (
	list: SkuVariant[] | undefined,
	variant: SkuVariant,
): SkuVariant[] => {
	const index = (list ?? []).findIndex((v) => v._id === variant._id);
	if (index === -1) return [...(list ?? []), variant];
	const next = [...(list ?? [])];
	next[index] = variant;
	return next;
};

interface SkuDetailsProps {
	onClose?: (saved?: boolean) => void;
}

function SkuDetails({ onClose }: SkuDetailsProps) {
	const { code } = useParams<{ code: string }>();
	const navigate = useNavigate();
	const isModal = !!onClose;
	const isNew = !code || code === 'new';

	const [values, setValues] = useState<Sku | null>(null);
	const [loadingSku, setLoadingSku] = useState(!isNew);
	const [img, setimg] = useState<EvidenceDocument<SkuImageType>[]>([]);
	const [initial, setInitial] =
		useState<Partial<CreateSkuDto>>(skuDetailsDefaults);
	const [brands, setBrands] = useState<
		{
			label: string;
			value: string;
			abbreviation: string;
			numericId: number;
			models?: Model[];
		}[]
	>([]);
	const [models, setModels] = useState<
		{
			label: string;
			value: string;
			numericId: number;
			cylinderCapacity: string[];
		}[]
	>([]);
	const [cylinders, setCylinders] = useState<
		{ label: string; value: string | number }[]
	>([]);
	const [selectedBrandInfo, setSelectedBrandInfo] = useState<{
		name: string;
		abbreviation: string;
		numericId: number;
	} | null>(null);
	const [selectedModelInfo, setSelectedModelInfo] = useState<{
		name: string;
		numericId: number;
	} | null>(null);
	const [nextVersionId, setNextVersionId] = useState<number | null>(null);
	const [showErrors, setShowErrors] = useState<boolean>(false);
	const [savingBasic, setSavingBasic] = useState<boolean>(false);
	const [savingSpecs, setSavingSpecs] = useState<boolean>(false);
	const [savingImages, setSavingImages] = useState<boolean>(false);
	const [savingDetailImages, setSavingDetailImages] = useState<boolean>(false);
	const [specs, setSpecs] =
		useState<Partial<CreateVehicleDetailDto>>(specsDefaults);
	const [initialSpecs, setInitialSpecs] =
		useState<Partial<CreateVehicleDetailDto>>(specsDefaults);
	const [existingDetailId, setExistingDetailId] = useState<string | null>(null);
	const [detailImgs, setDetailImgs] = useState<
		EvidenceDocument<VehicleDetailImagesType>[]
	>([]);
	const [specsOpen, setSpecsOpen] = useState<boolean>(true);
	const [photosOpen, setPhotosOpen] = useState<boolean>(true);
	const specsDirty = JSON.stringify(specs) !== JSON.stringify(initialSpecs);

	const [variantDraft, setVariantDraft] = useState<SkuVariantFormValues>({});
	const [variantModalOpen, setVariantModalOpen] = useState(false);
	const [editingVariant, setEditingVariant] = useState<SkuVariant | null>(null);
	const providers = useProviders();

	const finish = (saved?: boolean) => {
		if (onClose) {
			onClose(saved);
			return;
		}
		navigate('/sku');
	};

	useEffect(() => {
		if (isNew) {
			setLoadingSku(false);
			return;
		}
		setLoadingSku(true);
		getSkuByCode(code as string)
			.then((sku) => setValues(sku))
			.catch(() => {
				toasterNotification({
					msg: 'SKU no encontrado',
					toasterType: toasterOptions.error,
					style: 'light',
				});
				finish();
			})
			.finally(() => setLoadingSku(false));
	}, [code]);

	const findModels = (brandId: string, preselectedModelId?: string) => {
		const brandData = brands.find((e) => e.value === brandId);
		setSelectedBrandInfo(
			brandData
				? {
						name: brandData.label,
						abbreviation: brandData.abbreviation,
						numericId: brandData.numericId,
				  }
				: null,
		);
		const modelList =
			brandData?.models?.map((ele: Model) => ({
				label: ele.name,
				value: ele['_id'],
				numericId: ele.numericId,
				cylinderCapacity: ele.cylinderCapacity,
			})) ?? [];
		setModels(modelList);

		if (preselectedModelId) {
			const modelData = modelList.find((e) => e.value === preselectedModelId);
			setSelectedModelInfo(
				modelData
					? { name: modelData.label, numericId: modelData.numericId }
					: null,
			);
			setCylinders(
				modelData?.cylinderCapacity.map((ele: string) => ({
					label: ele,
					value: ele,
				})) ?? [],
			);
		} else {
			setSelectedModelInfo(null);
		}
	};

	const refreshNextVersionId = (modelId: string, version?: string) => {
		if (!values?._id && modelId) {
			setNextVersionId(null);
			getNextSkuVersionId(modelId, version || undefined)
				.then((nextId) => setNextVersionId(nextId))
				.catch(() => setNextVersionId(null));
		}
	};

	const findCylinders = (modelId: string) => {
		if (modelId && models) {
			const modelData = models.find((e) => e.value === modelId);
			setSelectedModelInfo(
				modelData
					? { name: modelData.label, numericId: modelData.numericId }
					: null,
			);
			setCylinders(
				modelData?.cylinderCapacity.map((ele: string) => ({
					label: ele,
					value: ele,
				})) ?? [],
			);
			refreshNextVersionId(modelId, form.values.version);
		}
	};

	useEffect(() => {
		getBrandsWithModels().then((res) => {
			if (res) {
				const maped = res
					.map((ele) => ({
						label: ele.name,
						value: ele['_id'],
						abbreviation: ele.abbreviation,
						numericId: ele.numericId,
						models: ele.models,
					}))
					.sort((a: any, b: any) => {
						if (a.label < b.label) return -1;
						if (a.label > b.label) return 1;
						return 0;
					});
				setBrands(maped);
			}
		});
	}, []);

	useEffect(() => {
		if (initial.brand) findModels(initial.brand, initial.model as string);
	}, [initial, brands]);

	useEffect(() => {
		let initialValue: Partial<CreateSkuDto> = skuDetailsDefaults;
		setSpecs(specsDefaults);
		setInitialSpecs(specsDefaults);
		setExistingDetailId(null);
		setDetailImgs([]);
		if (values?._id) {
			setimg(mapSkuImages(values.images));
			initialValue = {
				brand: values.brand,
				model: values.model,
				year: values.year,
				cc: values.cc,
				version: values.version,
				generalDescription: values.generalDescription,
				status: values.status ?? SkuStatusEnum.ACTIVE,
			};
			getVehicleDetailsBySkuId(values._id).then(
				(detail: VehicleDetail | null) => {
					if (detail) {
						setExistingDetailId(detail._id);
						const loadedSpecs: Partial<CreateVehicleDetailDto> = {
							engineType: detail.engineType,
							maxSpeedKmh: detail.maxSpeedKmh,
							maxPower: detail.maxPower,
							maxTorque: detail.maxTorque,
							fuelEfficiencyKml: detail.fuelEfficiencyKml,
							fuelSystem: detail.fuelSystem,
							fuelCapacityLiters: detail.fuelCapacityLiters,
							transmissionType: detail.transmissionType,
							transmissionSpeeds: detail.transmissionSpeeds,
							coolingSystem: detail.coolingSystem,
							frontSuspension: detail.frontSuspension,
							rearSuspension: detail.rearSuspension,
							frontBrakes: detail.frontBrakes,
							rearBrakes: detail.rearBrakes,
							frontTire: detail.frontTire,
							rearTire: detail.rearTire,
							dimensions: detail.dimensions,
						};
						setSpecs(loadedSpecs);
						setInitialSpecs(loadedSpecs);
						setDetailImgs(mapDetailImages(detail.images));
					}
				},
			);
		}
		setInitial(initialValue);
	}, [values]);

	const handleImageAdd = async (
		e: ChangeEvent<HTMLInputElement>,
		fileType: SkuImageType,
	) => {
		const { files } = e.target;
		if (!files || savingImages) return;

		const validFiles: File[] = [];
		// eslint-disable-next-line no-plusplus
		for (let i = 0; i < files.length; i++) {
			const file = files.item(i);
			if (file) {
				if (file.size > 10000000) {
					toasterNotification({
						msg: 'La imagen no debe ser superior a 10mb',
						toasterType: toasterOptions.warning,
						style: 'light',
					});
				} else {
					validFiles.push(file);
				}
			}
		}
		if (!validFiles.length) return;

		if (!values?._id) {
			setimg((prev) => [
				...prev,
				...validFiles.map((file) => ({
					file,
					url: URL.createObjectURL(file),
					mimeType: 'image',
					_id: 'temp',
					typeEvidence: fileType,
					name: file.name,
					ext: file.type,
					size: file.size,
					path: '',
					hash: '',
					createdAt: '',
				})),
			]);
			return;
		}

		setSavingImages(true);
		const toastId = toasterNotification({
			msg: 'Subiendo imagen...',
			toasterType: toasterOptions.info,
			style: 'light',
		});

		try {
			// eslint-disable-next-line no-restricted-syntax
			for (const file of validFiles) {
				// eslint-disable-next-line no-await-in-loop
				await addImageToSku(values._id, fileType, file)
					.then((sku) => {
						setValues(sku);
						setimg(mapSkuImages(sku.images));
					})
					.catch(() => {
						toasterNotification({
							msg: 'No se pudo subir la imagen',
							toasterType: toasterOptions.error,
							style: 'light',
						});
					});
			}
			toast.dismiss(toastId);
			toasterNotification({
				msg: 'Imagen actualizada',
				toasterType: toasterOptions.success,
				style: 'light',
			});
		} finally {
			setSavingImages(false);
		}
	};

	const handleImageDelete = (doc: EvidenceDocument<SkuImageType>) => {
		if (savingImages) return;
		if (!values?._id) {
			setimg((prev) => prev.filter((d) => d !== doc));
			return;
		}

		setSavingImages(true);
		const toastId = toasterNotification({
			msg: 'Eliminando imagen...',
			toasterType: toasterOptions.info,
			style: 'light',
		});

		deleteSkuImage(values._id, doc._id)
			.then((sku) => {
				setValues(sku);
				setimg(mapSkuImages(sku.images));
				toasterNotification({
					msg: 'Imagen eliminada',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch(() => {
				toasterNotification({
					msg: 'No se pudo eliminar la imagen',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			})
			.finally(() => {
				toast.dismiss(toastId);
				setSavingImages(false);
			});
	};

	const handleDetailFiles = (
		e: ChangeEvent<HTMLInputElement>,
		fileType: VehicleDetailImagesType,
	) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 10000000) {
			toasterNotification({
				msg: 'La imagen no debe ser superior a 10mb',
				toasterType: toasterOptions.warning,
				style: 'light',
			});
			return;
		}
		if (!values?._id) return;

		const uploadTo = (detailId: string) =>
			addFileVehicleDetail(detailId, fileType, file)
				.then((detail) => {
					setDetailImgs(mapDetailImages(detail.images));
					toasterNotification({
						msg: 'Foto guardada',
						toasterType: toasterOptions.success,
						style: 'light',
					});
				})
				.catch(() => {
					toasterNotification({
						msg: 'No se pudo subir la foto',
						toasterType: toasterOptions.error,
						style: 'light',
					});
				});

		if (existingDetailId) {
			uploadTo(existingDetailId);
			return;
		}
		createVehicleDetails({ sku: values._id } as CreateVehicleDetailDto)
			.then((detail) => {
				setExistingDetailId(detail._id);
				return uploadTo(detail._id);
			})
			.catch(() => {
				toasterNotification({
					msg: 'No se pudo crear el registro de especificaciones',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			});
	};

	const handleDetailImageDelete = (
		doc: EvidenceDocument<VehicleDetailImagesType>,
	) => {
		if (savingDetailImages || !existingDetailId) return;

		setSavingDetailImages(true);
		const toastId = toasterNotification({
			msg: 'Eliminando imagen...',
			toasterType: toasterOptions.info,
			style: 'light',
		});

		deleteFileVehicleDetail(existingDetailId, doc._id)
			.then((detail) => {
				setDetailImgs(mapDetailImages(detail.images));
				toasterNotification({
					msg: 'Imagen eliminada',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch(() => {
				toasterNotification({
					msg: 'No se pudo eliminar la imagen',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			})
			.finally(() => {
				toast.dismiss(toastId);
				setSavingDetailImages(false);
			});
	};

	const onSaveSpecs = () => {
		if (!values?._id) return;
		setSavingSpecs(true);
		const save = existingDetailId
			? updateVehicleDetails(existingDetailId, specs).then(
					() => existingDetailId,
			  )
			: createVehicleDetails({
					...specs,
					sku: values._id,
			  } as CreateVehicleDetailDto).then((d) => d._id);

		save
			.then((detailId) => {
				setExistingDetailId(detailId);
				setInitialSpecs(specs);
				setSavingSpecs(false);
				toasterNotification({
					msg: 'Especificaciones guardadas',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch(() => {
				setSavingSpecs(false);
				toasterNotification({
					msg: 'No se pudieron guardar las especificaciones',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			});
	};

	const onSubmitBasic = (vals: Partial<CreateSkuDto>) => {
		if (!values?._id && !img.length) {
			toasterNotification({
				msg: 'Debes subir al menos una imagen del vehículo',
				toasterType: toasterOptions.error,
				style: 'light',
			});
			setShowErrors(true);
			return;
		}

		if (!values?._id && (!variantDraft.price || variantDraft.price <= 0)) {
			toasterNotification({
				msg: 'Debes ingresar el precio de la variante inicial',
				toasterType: toasterOptions.error,
				style: 'light',
			});
			setShowErrors(true);
			return;
		}

		setSavingBasic(true);

		if (!values?._id) {
			createSku({
				...(vals as CreateSkuDto),
				variant: {
					provider: variantDraft.provider || undefined,
					color: variantDraft.color || undefined,
					hasGps: variantDraft.hasGps ?? false,
					price: variantDraft.price as number,
				},
			})
				.then(async (sku) => {
					let updatedSku = sku;
					const filesToUpload = img.filter((image) => image.file);
					// eslint-disable-next-line no-restricted-syntax
					for (const image of filesToUpload) {
						// eslint-disable-next-line no-await-in-loop
						updatedSku = await addImageToSku(
							sku._id,
							SkuImageType.catalogPhoto,
							image.file as File,
						);
					}
					return updatedSku;
				})
				.then((sku) => {
					setSavingBasic(false);
					toasterNotification({
						msg: 'SKU creado correctamente',
						toasterType: toasterOptions.success,
						style: 'light',
					});
					navigate(`/sku/${sku.code}`);
				})
				.catch((err) => {
					setSavingBasic(false);
					toasterNotification({
						msg:
							typeof err?.message === 'string'
								? err.message
								: 'Ocurrió un error',
						toasterType: toasterOptions.error,
						style: 'light',
					});
				});
			return;
		}

		updateSku(values._id, vals)
			.then((sku) => {
				setValues(sku);
				setSavingBasic(false);
				toasterNotification({
					msg: 'SKU actualizado',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch((err) => {
				setSavingBasic(false);
				toasterNotification({
					msg:
						typeof err?.message === 'string' ? err.message : 'Ocurrió un error',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			});
	};

	const form = useFormik<Partial<CreateSkuDto>>({
		initialValues: initial,
		validationSchema: skuDetailsValidationSchema,
		onSubmit: onSubmitBasic,
		enableReinitialize: true,
		validateOnMount: true,
	});

	const brandPart = selectedBrandInfo
		? `${selectedBrandInfo.abbreviation}${String(
				selectedBrandInfo.numericId,
		  ).padStart(2, '0')}`
		: '???';
	const getVersionPart = () => {
		if (values?._id) return String(values.numericId ?? '??').padStart(2, '0');
		if (nextVersionId != null) return String(nextVersionId).padStart(2, '0');
		return '??';
	};
	const versionPart = getVersionPart();
	const skuPreview = `${brandPart}-${form.values.year ?? '????'}-${
		selectedModelInfo
			? String(selectedModelInfo.numericId).padStart(2, '0')
			: '??'
	}-${versionPart}-${form.values.cc ?? '???'}`;
	const skuDescription = [
		selectedBrandInfo?.name,
		form.values.year,
		selectedModelInfo?.name,
		form.values.cc ? `${form.values.cc}cc` : undefined,
		form.values.version || undefined,
	]
		.filter(Boolean)
		.join(' ');

	if (loadingSku) {
		return (
			<div className="dim_100_per flex_center_col shadow_hard inner_container bg_neutral_400">
				<Typography
					weight="600"
					scale="heading4"
					textColor="neutral_800"
					className="m_b_xs"
				>
					Cargando SKU...
				</Typography>
				<img src={motoLoading} alt="" className="h_xxxl" />
			</div>
		);
	}

	const basicInfoActions = (
		<>
			<Button
				variant="outline"
				scale="small"
				className="flex_grow_1"
				onClick={() => finish()}
				disabled={savingBasic || savingImages}
			>
				Cancelar
			</Button>
			<Button
				scale="small"
				icon={<Save />}
				className="flex_grow_1"
				loading={savingBasic}
				disabled={savingImages}
				onClick={() => {
					if (!form.isValid) setShowErrors(true);
					form.handleSubmit();
				}}
			>
				{!values?._id ? 'Crear SKU' : 'Guardar cambios'}
			</Button>
		</>
	);

	const formContent = (
		<>
			<Grid container direction="row" spacing={3}>
				<Grid item xs={12} md={!values?._id ? 12 : 7}>
					<FormikProvider value={form}>
						{isModal && (
							<div className="flex_header">
								<Typography
									scale="heading4"
									weight="600"
									textColor="neutral_900"
									className="m_b_lg m_t_md"
								>
									Añadir SKU
								</Typography>
								<Divider />
							</div>
						)}
						<div className="flex_body overflow_y_auto p_y_lg p_x_sm">
							<Grid container direction="row" spacing={3}>
								{/* Vista previa del SKU */}
								<Grid item xs={12}>
									<div
										style={{
											background: '#F0F4FF',
											border: '1px solid #C7D6FA',
											borderRadius: '8px',
											padding: '16px',
										}}
									>
										<Typography
											scale="xsmall"
											weight="400"
											textColor="neutral_600"
											className="m_b_xxs"
										>
											Vista previa del SKU
										</Typography>
										<Typography
											scale="heading3"
											weight="600"
											textColor="primary_300"
										>
											{skuPreview}
										</Typography>
										{skuDescription && (
											<Typography
												scale="xsmall"
												weight="400"
												textColor="neutral_700"
												className="m_t_xxs"
											>
												{skuDescription}
											</Typography>
										)}
									</div>
								</Grid>

								{/* Campos del SKU */}
								<Grid item xs={12}>
									<Typography
										scale="large"
										weight="600"
										textColor="neutral_700"
										className="m_b_lg"
									>
										Información del SKU
									</Typography>
								</Grid>

								<Grid item xs={4}>
									<Input
										onBlur={form.handleBlur}
										error={
											!!form.errors.brand &&
											(form.getFieldMeta('brand').touched || showErrors)
										}
										subtitle={
											form.getFieldMeta('brand').touched || showErrors
												? form.errors.brand
												: undefined
										}
										title="Marca"
										placeholder="Selecciona marca"
										name="brand"
										type="select"
										options={brands}
										icon={<Bookmark />}
										className="m_b_xs w_100_per"
										value={form.values.brand}
										onChange={(e: any) => {
											findModels(e.target.value);
											form.handleChange(e);
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<Input
										onBlur={form.handleBlur}
										error={
											!!form.errors.model &&
											(form.getFieldMeta('model').touched || showErrors)
										}
										subtitle={
											form.getFieldMeta('model').touched || showErrors
												? form.errors.model
												: undefined
										}
										title="Modelo"
										placeholder="Modelo del vehículo"
										name="model"
										type="select"
										options={models as any[]}
										icon={<Moto />}
										className="m_b_xs w_100_per"
										value={form.values.model}
										onChange={(e: any) => {
											findCylinders(e.target.value);
											form.handleChange(e);
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<Input
										onBlur={(e: any) => {
											form.handleBlur(e);
											if (form.values.model) {
												refreshNextVersionId(form.values.model, e.target.value);
											}
										}}
										error={
											!!form.errors.version &&
											(form.getFieldMeta('version').touched || showErrors)
										}
										subtitle={
											form.getFieldMeta('version').touched || showErrors
												? form.errors.version
												: undefined
										}
										title="Versión (opcional)"
										placeholder="Ej: I, N, Z"
										name="version"
										type="text"
										icon={<Moto />}
										className="m_b_xs w_100_per"
										value={form.values.version}
										onChange={(e: any) => {
											e.target.value = e.target.value.toUpperCase();
											form.handleChange(e);
										}}
									/>
								</Grid>
								<Grid item xs={4}>
									<Input
										onBlur={form.handleBlur}
										error={
											!!form.errors.year &&
											(form.getFieldMeta('year').touched || showErrors)
										}
										subtitle={
											form.getFieldMeta('year').touched || showErrors
												? form.errors.year
												: undefined
										}
										title="Año"
										placeholder="Año del modelo"
										name="year"
										type="select"
										options={yearOptions}
										icon={<Calendar />}
										className="m_b_xs w_100_per"
										value={form.values.year}
										onChange={form.handleChange}
									/>
								</Grid>
								<Grid item xs={4}>
									<Input
										onBlur={form.handleBlur}
										error={
											!!form.errors.cc &&
											(form.getFieldMeta('cc').touched || showErrors)
										}
										subtitle={
											form.getFieldMeta('cc').touched || showErrors
												? form.errors.cc
												: undefined
										}
										title="Cilindraje"
										name="cc"
										type="select"
										options={cylinders}
										icon={<Speedometer />}
										className="m_b_xs w_100_per"
										value={form.values.cc}
										onChange={form.handleChange}
										placeholder="Cilindraje"
									/>
								</Grid>
								<Grid item xs={4}>
									<Input
										title="Estado en catálogo"
										name="status"
										type="select"
										options={[
											{ value: SkuStatusEnum.ACTIVE, label: 'Activo' },
											{ value: SkuStatusEnum.INACTIVE, label: 'Inactivo' },
										]}
										className="m_b_xs w_100_per"
										value={form.values.status ?? SkuStatusEnum.ACTIVE}
										onChange={form.handleChange}
									/>
								</Grid>
								<Grid item xs={12}>
									<Input
										onBlur={form.handleBlur}
										error={
											!!form.errors.generalDescription &&
											(form.getFieldMeta('generalDescription').touched ||
												showErrors)
										}
										subtitle={
											form.getFieldMeta('generalDescription').touched ||
											showErrors
												? form.errors.generalDescription
												: undefined
										}
										title="Descripción general"
										placeholder="Datos adicionales del SKU, distintos de la descripción autogenerada"
										name="generalDescription"
										type="textarea"
										className="m_b_xs w_100_per"
										value={form.values.generalDescription}
										onChange={form.handleChange}
										inputClassName="h_100_px"
									/>
								</Grid>

								{/* Imágenes del vehículo — mínimo 1 requerida, se guardan al instante en edición */}
								<Grid item xs={12}>
									<div className="display_flex flex_align_center flex_gap_xs m_b_xs">
										<Typography
											scale="large"
											weight="600"
											textColor="neutral_700"
										>
											Imágenes del vehículo
										</Typography>
									</div>
									{showErrors && !img.length && (
										<Typography
											scale="xsmall"
											weight="400"
											textColor="red_300"
											className="m_b_xs"
										>
											Debes subir al menos una imagen
										</Typography>
									)}
									<Grid container direction="row" spacing={1}>
										{img.map((image, index) => (
											// eslint-disable-next-line react/no-array-index-key
											<Grid
												item
												xs={4}
												className="p_md"
												key={image._id ?? index}
											>
												<div className="pos_relative">
													<div className="w_100_per dso_card overflow_hidden document_card">
														<img
															src={image.url}
															alt={image.name}
															className="image center_x"
														/>
													</div>
													<Close
														className="dim_lg delete_file_solicitud"
														style={
															savingImages
																? { opacity: 0.5, pointerEvents: 'none' }
																: undefined
														}
														onClick={() => handleImageDelete(image)}
													/>
												</div>
											</Grid>
										))}
										<Grid item xs={4} className="p_md">
											<div
												className="w_100_per dso_card border_primary_200 border_dashed p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
												style={savingImages ? { opacity: 0.5 } : undefined}
											>
												<AddCircle className="dim_xl text_primary_300" />
												<Typography
													scale="small"
													weight="600"
													className="text_center"
													textColor="primary_300"
												>
													Añadir imagen
												</Typography>
												<input
													type="file"
													accept="image/png, image/jpeg"
													multiple
													disabled={savingImages}
													onChange={(e) =>
														handleImageAdd(e, SkuImageType.catalogPhoto)
													}
													className="input-file"
												/>
											</div>
										</Grid>
									</Grid>
								</Grid>

								{!values?._id && (
									<Grid item xs={12}>
										<Divider className="m_b_lg" />
										<Typography
											scale="large"
											weight="600"
											textColor="neutral_700"
											className="m_b_xs"
										>
											Variante inicial
										</Typography>
										<SkuVariantDetails
											onChange={setVariantDraft}
											showErrors={showErrors}
										/>
									</Grid>
								)}

								{!isModal && (
									<Grid item xs={12}>
										<div className="display_flex flex_gap_sm flex_justify_end">
											{basicInfoActions}
										</div>
									</Grid>
								)}
							</Grid>
						</div>
						{isModal && (
							<div className="flex_footer display_flex flex_gap_sm p_md">
								{basicInfoActions}
							</div>
						)}
					</FormikProvider>

					<Divider className="m_b_lg" />
					{values?._id && (
						<div className="p_x_sm p_y_lg">
							<div className="display_flex flex_justify_between flex_align_center">
								<div className="display_flex flex_gap_sm flex_align_center">
									<PriceTag className="dim_lg text_primary_300" />
									<Typography
										scale="large"
										weight="600"
										textColor="neutral_700"
									>
										Variantes
									</Typography>
								</div>

								<Button
									variant="tint"
									scale="small"
									icon={<AddCircle />}
									onClick={() => {
										setEditingVariant(null);
										setVariantModalOpen(true);
									}}
								>
									Añadir variante
								</Button>
							</div>
							<div className="m_t_lg">
								{(values.variants ?? []).map((variant) => (
									<div
										key={variant._id}
										className="display_flex flex_justify_between flex_align_center p_sm border_solid border_1 border_neutral_400 br_xs m_b_xs"
									>
										<div>
											<Typography
												scale="medium"
												weight="600"
												textColor="neutral_900"
											>
												${variant.price}
												{variant.status === SkuStatusEnum.INACTIVE && (
													<Typography
														scale="xsmall"
														weight="400"
														textColor="red_300"
														className="bg_red_100 p_x_xs p_y_xxs br_xxs m_l_xs"
														component="span"
													>
														Inactiva
													</Typography>
												)}
											</Typography>
											<Typography
												scale="xsmall"
												weight="400"
												textColor="neutral_600"
											>
												{[
													providers.find((p) => p._id === variant.provider)
														?.name,
													variant.color
														? vehicleColorValues[variant.color]
														: undefined,
													variant.hasGps ? 'GPS' : undefined,
												]
													.filter(Boolean)
													.join(' · ') || 'Sin diferenciadores'}
											</Typography>
										</div>
										<div className="display_flex flex_wrap flex_gap_sm flex_align_center">
											<Button
												variant="outline"
												scale="small"
												onClick={() => {
													setEditingVariant(variant);
													setVariantModalOpen(true);
												}}
											>
												Editar
											</Button>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</Grid>

				{values?._id && (
					<Grid item xs={5}>
						<div className="p_x_sm p_y_lg">
							<div
								className="display_flex flex_justify_between flex_align_center cursor_pointer"
								role="button"
								tabIndex={0}
								onClick={() => setSpecsOpen((prev) => !prev)}
								onKeyDown={() => setSpecsOpen((prev) => !prev)}
							>
								<div className="display_flex flex_gap_sm flex_align_center">
									<Details className="dim_lg text_primary_300" />
									<Typography
										scale="large"
										weight="600"
										textColor="neutral_700"
									>
										Especificaciones técnicas
									</Typography>
								</div>
								<ChevronDown
									className="dim_md"
									style={{
										transform: specsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
										transition: 'transform 0.2s ease',
									}}
								/>
							</div>
							{specsOpen && (
								<Grid container direction="row" spacing={3} className="m_t_lg">
									<Grid item xs={6}>
										<Input
											title="Tipo de motor"
											placeholder="Ej: 4-stroke"
											name="engineType"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.engineType ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, engineType: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Velocidad máx. (km/h)"
											placeholder="Ej: 120"
											name="maxSpeedKmh"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.maxSpeedKmh ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													maxSpeedKmh: Number(e.target.value),
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Potencia máx."
											placeholder="Ej: 15 HP @ 8000 RPM"
											name="maxPower"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.maxPower ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, maxPower: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Torque máx."
											placeholder="Ej: 12 Nm @ 6000 RPM"
											name="maxTorque"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.maxTorque ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, maxTorque: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Eficiencia combustible (km/L)"
											placeholder="Ej: 35"
											name="fuelEfficiencyKml"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.fuelEfficiencyKml ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													fuelEfficiencyKml: Number(e.target.value),
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Sistema de combustible"
											placeholder="Ej: Fuel Injection"
											name="fuelSystem"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.fuelSystem ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, fuelSystem: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Capacidad combustible (L)"
											placeholder="Ej: 12"
											name="fuelCapacityLiters"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.fuelCapacityLiters ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													fuelCapacityLiters: Number(e.target.value),
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Tipo de transmisión"
											placeholder="Ej: Manual"
											name="transmissionType"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.transmissionType ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													transmissionType: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Velocidades de transmisión"
											placeholder="Ej: 5"
											name="transmissionSpeeds"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.transmissionSpeeds ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													transmissionSpeeds: Number(e.target.value),
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Sistema de enfriamiento"
											placeholder="Ej: Liquid cooled"
											name="coolingSystem"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.coolingSystem ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, coolingSystem: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Suspensión delantera"
											placeholder="Ej: Telescopic fork"
											name="frontSuspension"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.frontSuspension ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													frontSuspension: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Suspensión trasera"
											placeholder="Ej: Mono shock"
											name="rearSuspension"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.rearSuspension ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, rearSuspension: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Frenos delanteros"
											placeholder="Ej: Disc brake"
											name="frontBrakes"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.frontBrakes ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, frontBrakes: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Frenos traseros"
											placeholder="Ej: Disc brake"
											name="rearBrakes"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.rearBrakes ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, rearBrakes: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Llanta delantera"
											placeholder="Ej: 100/80-17"
											name="frontTire"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.frontTire ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, frontTire: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Llanta trasera"
											placeholder="Ej: 130/70-17"
											name="rearTire"
											type="text"
											className="m_b_xs w_100_per"
											value={specs.rearTire ?? ''}
											onChange={(e: any) =>
												setSpecs({ ...specs, rearTire: e.target.value })
											}
										/>
									</Grid>
									<Grid item xs={12}>
										<Typography
											scale="medium"
											weight="600"
											textColor="neutral_700"
											className="m_b_xs"
										>
											Dimensiones
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Largo (mm)"
											placeholder="Ej: 2000"
											name="lengthMm"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.lengthMm ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														lengthMm: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Ancho (mm)"
											placeholder="Ej: 800"
											name="widthMm"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.widthMm ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														widthMm: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Alto (mm)"
											placeholder="Ej: 1200"
											name="heightMm"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.heightMm ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														heightMm: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Distancia entre ejes (mm)"
											placeholder="Ej: 1400"
											name="wheelbaseMm"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.wheelbaseMm ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														wheelbaseMm: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Altura del asiento (mm)"
											placeholder="Ej: 800"
											name="seatHeightMm"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.seatHeightMm ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														seatHeightMm: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<Input
											title="Capacidad de carga (kg)"
											placeholder="Ej: 150"
											name="loadCapacityKg"
											type="number"
											className="m_b_xs w_100_per"
											value={specs.dimensions?.loadCapacityKg ?? ''}
											onChange={(e: any) =>
												setSpecs({
													...specs,
													dimensions: {
														...specs.dimensions,
														loadCapacityKg: Number(e.target.value),
													} as any,
												})
											}
										/>
									</Grid>
									{specsDirty && (
										<Grid item xs={12}>
											<div className="display_flex flex_justify_end">
												<Button
													scale="small"
													icon={<Save />}
													loading={savingSpecs}
													disabled={savingImages}
													onClick={onSaveSpecs}
												>
													Guardar especificaciones
												</Button>
											</div>
										</Grid>
									)}
								</Grid>
							)}
						</div>

						<Divider />

						<div className="p_x_sm p_y_lg">
							<div
								className="display_flex flex_justify_between flex_align_center cursor_pointer"
								role="button"
								tabIndex={0}
								onClick={() => setPhotosOpen((prev) => !prev)}
								onKeyDown={() => setPhotosOpen((prev) => !prev)}
							>
								<div className="display_flex flex_gap_sm flex_align_center">
									<Images className="dim_lg text_primary_300" />
									<Typography
										scale="large"
										weight="600"
										textColor="neutral_700"
									>
										Fotos del detalle de la moto
									</Typography>
								</div>
								<ChevronDown
									className="dim_md"
									style={{
										transform: photosOpen ? 'rotate(180deg)' : 'rotate(0deg)',
										transition: 'transform 0.2s ease',
									}}
								/>
							</div>
							<div className="m_y_md">
								<Typography scale="medium" weight="600" textColor="primary_300">
									Ingresa al menos 3 imágenes para mostrar las especificaciones
									y las fotografías en el detalle del catálogo.
								</Typography>
							</div>
							{photosOpen && (
								<Grid container direction="row" spacing={1} className="m_t_lg">
									<LoadDocuments
										documents={detailImgs}
										onDelete={handleDetailImageDelete}
										onAdd={handleDetailFiles}
										availableDocs={Object.values(VehicleDetailImagesType)}
										docsValues={VehicleDetailImagesTypeValues}
										cols={2}
										canAddOther={false}
										canDelete={!savingDetailImages}
									/>
								</Grid>
							)}
						</div>
					</Grid>
				)}
			</Grid>
			<SideModal
				open={variantModalOpen}
				setOpen={setVariantModalOpen}
				setFullOpen={() => {}}
				size="small"
			>
				{variantModalOpen && values?._id && (
					<SkuVariantDetails
						skuCode={values.code}
						skuId={values._id}
						variant={editingVariant}
						onSaved={(saved) => {
							setValues((prev) =>
								prev
									? { ...prev, variants: upsertVariant(prev.variants, saved) }
									: prev,
							);
							setVariantModalOpen(false);
						}}
						onClose={() => setVariantModalOpen(false)}
					/>
				)}
			</SideModal>
		</>
	);

	if (isModal) {
		return formContent;
	}

	return (
		<div className="bg_neutral_0">
			<div className="p_x_lg p_y_md display_flex flex_gap_md flex_align_center">
				<Button
					variant="icon"
					icon={<Back />}
					subvariant="edit"
					scale="small"
					onClick={() => finish()}
					className="flex_align_self_start"
				/>
				<Typography scale="heading4" weight="600" textColor="neutral_900">
					Editar SKU
				</Typography>
			</div>
			<div className="p_x_lg p_y_md">{formContent}</div>
		</div>
	);
}

export default SkuDetails;
